import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Search, MessageCircle, Send, Phone, Calendar, ExternalLink, User, Loader2, Zap, Check, CheckCheck, Bot, X, Paperclip } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { waGetMessages, waSendMessage, getAutomationConfig, updateLeadField, waFlowSendToLead } from "./api";
import LeadAvatar from "./LeadAvatar";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_TABS = [
  { key: "all", label: "All", filter: () => true },
  { key: "unread", label: "Unread", filter: l => (l.waUnreadCount || 0) > 0 },
  { key: "new", label: "New", filter: l => l.status === "new" },
  { key: "upcoming", label: "Upcoming", filter: l => ["upcoming", "demo_in_progress", "no_show"].includes(l.status) },
  { key: "demo_done", label: "Demo done", filter: l => ["demo_done", "payment_sent"].includes(l.status) },
  { key: "converted", label: "Won", filter: l => l.status === "converted" },
];

export default function Inbox() {
  const { auth, live } = useOutletContext();
  const navigate = useNavigate();
  const { leadId: paramLeadId } = useParams();
  const isAdmin = auth?.username === "admin";

  const [selectedId, setSelectedId] = useState(paramLeadId || null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileInputRef = useRef(null);
  const [quickReplies, setQuickReplies] = useState([
    { id: "q1", label: "Book", text: "Great! When works for you this week?" },
    { id: "q2", label: "Payment", text: "Here's the payment link — drop me a screenshot once done!" },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    getAutomationConfig()
      .then((r) => {
        const qrs = (r?.config?.quickReplies || []).filter(q => q.label && q.text);
        if (qrs.length) setQuickReplies(qrs);
      })
      .catch(() => {});
  }, []);

  // Derive scoped + sorted leads from shared live data
  const allLeads = live?.leads || [];
  const leads = React.useMemo(() => {
    const scoped = isAdmin ? allLeads : allLeads.filter(l => l.closerUsername === auth?.username);
    return [...scoped].sort((a, b) => {
      const au = (a.waUnreadCount || 0) > 0, bu = (b.waUnreadCount || 0) > 0;
      if (au !== bu) return bu - au;
      return new Date(b.lastWaMessageAt || b.createdAt || 0) - new Date(a.lastWaMessageAt || a.createdAt || 0);
    });
  }, [allLeads, isAdmin, auth?.username]);

  useEffect(() => {
    if (live?.leads !== undefined) setLoading(false);
    if (!selectedId && leads.length > 0) setSelectedId(leads[0].id);
  }, [live?.leads, leads, selectedId]);

  const loadLeads = useCallback(() => live?.refresh?.(), [live]);

  const loadMessages = useCallback(async () => {
    if (!selectedId) { setMessages([]); return; }
    try {
      const res = await waGetMessages(selectedId);
      const msgs = Array.isArray(res) ? res : (res?.messages || []);
      setMessages(msgs);
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }));
    } catch (e) { /* keep */ }
  }, [selectedId]);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 6000);
    return () => clearInterval(t);
  }, [loadMessages]);

  // Mark as read when opened
  useEffect(() => {
    if (!selectedId) return;
    fetch(`${API}/api/demo/whatsapp/mark-read/${selectedId}`, { method: "POST" }).catch(() => {});
  }, [selectedId]);

  const selected = leads.find(l => l.id === selectedId);
  const selectedDisplayName = selected
    ? ((selected.clientName && selected.clientName.trim()) ? `${selected.clientName} ${selected.clientLastName || ""}`.trim() : (selected.whatsappNumber || "Unknown"))
    : "";
  const filtered = leads
    .filter(STATUS_TABS.find(t => t.key === tab).filter)
    .filter(l => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (l.clientName || "").toLowerCase().includes(s) ||
        (l.clientLastName || "").toLowerCase().includes(s) ||
        (l.whatsappNumber || "").includes(s) ||
        (l.targetRole || "").toLowerCase().includes(s)
      );
    });

  const unreadCount = leads.filter(l => (l.waUnreadCount || 0) > 0).length;

  const handleSend = async () => {
    if (!draft.trim() || !selected?.whatsappNumber) return;
    setSending(true);
    try {
      await waSendMessage(selected.whatsappNumber, draft, selected.id);
      setDraft("");
      await loadMessages();
    } catch (e) {
      // surface error
      alert("Send failed: " + (e?.message || "Check Meta 24h window / template fallback"));
    } finally { setSending(false); }
  };

  const handleMediaPick = () => {
    if (!selected?.id) return;
    fileInputRef.current?.click();
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file || !selected?.id) return;
    if (file.size > 100 * 1024 * 1024) { alert("File too large (max 100 MB)"); return; }
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("leadId", selected.id);
      if (draft.trim()) fd.append("caption", draft.trim());
      const r = await fetch(`${API}/api/demo/whatsapp/send-media`, { method: "POST", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${r.status})`);
      }
      setDraft("");
      await loadMessages();
    } catch (err) {
      alert("Media send failed: " + (err?.message || "Unknown error"));
    } finally {
      setUploadingMedia(false);
    }
  };

  // quickReplies now loaded from dynamic automation config (Automation Studio → Quick Replies tab)

  return (
    <div className="inbox-root" data-testid="inbox-root">
      {/* ─── Left rail: conversation list ─── */}
      <aside className="inbox-list">
        <div className="inbox-list-header">
          <h2>Inbox {unreadCount > 0 && <span className="inbox-unread-badge">{unreadCount}</span>}</h2>
        </div>
        <div className="inbox-search">
          <Search className="w-3.5 h-3.5" />
          <input
            placeholder="Search name, phone, role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="inbox-search"
          />
        </div>
        <div className="inbox-tabs" data-testid="inbox-tabs">
          {STATUS_TABS.map(t => (
            <button
              key={t.key}
              className={`inbox-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
              data-testid={`inbox-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="inbox-list-body">
          {loading ? (
            <div className="inbox-empty"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="inbox-empty">
              <MessageCircle className="w-6 h-6" />
              <span>No conversations match</span>
            </div>
          ) : filtered.map(l => {
            const displayName = (l.clientName && l.clientName.trim()) ? `${l.clientName} ${l.clientLastName || ""}`.trim() : (l.whatsappNumber || "Unknown");
            return (
            <div
              key={l.id}
              className={`inbox-item ${selectedId === l.id ? "active" : ""} ${(l.waUnreadCount || 0) > 0 ? "unread" : ""}`}
              onClick={() => setSelectedId(l.id)}
              data-testid={`inbox-item-${l.id}`}
            >
              <LeadAvatar lead={l} size={49} />
              <div className="inbox-item-main">
                <div className="inbox-item-row1">
                  <span className="inbox-name">{displayName}</span>
                  <span className="inbox-time">{relativeTime(l.lastWaMessageAt)}</span>
                </div>
                <div className="inbox-item-row2">
                  <span className="inbox-preview">
                    {(l.lastWaMessageBody || l.notes || l.targetRole || "—").substring(0, 60)}
                  </span>
                  {(l.waUnreadCount || 0) > 0 && (
                    <span className="inbox-count">{l.waUnreadCount}</span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </aside>

      {/* ─── Middle: chat thread ─── */}
      <main className="inbox-chat">
        {!selected ? (
          <div className="inbox-no-selection">
            <MessageCircle className="w-10 h-10" />
            <h3>Select a conversation</h3>
            <p>Pick a lead from the left to see their full chat history and reply.</p>
          </div>
        ) : (
          <>
            <div className="inbox-chat-header">
              <div className="inbox-chat-user">
                <div
                  className="inbox-avatar-clickable"
                  title="Click to set custom profile picture URL"
                  onClick={async () => {
                    const url = prompt(
                      "Set profile picture URL for " + selectedDisplayName + " (LinkedIn pic, etc.):",
                      selected.profilePicUrl || ""
                    );
                    if (url === null) return;
                    const trimmed = url.trim();
                    try {
                      await updateLeadField(selected.id, { profilePicUrl: trimmed });
                      live?.refresh?.();
                    } catch (e) {
                      alert("Could not save: " + (e?.message || "unknown"));
                    }
                  }}
                  data-testid="inbox-chat-avatar-edit"
                >
                  <LeadAvatar lead={selected} size={44} className="inbox-avatar-lg" />
                </div>
                <div className="inbox-chat-user-meta">
                  <h3 data-testid="inbox-chat-lead-name">{selectedDisplayName}</h3>
                  <span className="inbox-chat-user-sub" data-testid="inbox-chat-lead-sub">
                    <span className="inbox-chat-phone">{selected.whatsappNumber}</span>
                    {selected.lastWaMessageAt && (
                      <span className="inbox-chat-lastseen">last message {relativeTime(selected.lastWaMessageAt)} ago</span>
                    )}
                  </span>
                </div>
              </div>
              <div className="inbox-chat-actions">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await waFlowSendToLead(selected.id);
                      alert("📅 Booking calendar sent to " + selectedDisplayName);
                    } catch (e) {
                      alert("Send failed: " + (e?.message || "Run booking flow setup first."));
                    }
                  }}
                  data-testid="inbox-send-booking-flow"
                  title="Send WhatsApp booking calendar (Calendly-style)"
                >
                  <Calendar className="w-3 h-3 mr-1" /> Send booking link
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/demo/lead/${selected.id}`)} data-testid="inbox-view-lead">
                  <User className="w-3 h-3 mr-1" /> Lead details
                </Button>
                {selected.status === "upcoming" && (
                  <Button size="sm" onClick={() => navigate(`/demo/live/${selected.id}`)} data-testid="inbox-open-demo">
                    <Zap className="w-3 h-3 mr-1" /> Open demo
                  </Button>
                )}
              </div>
            </div>

            <div className="inbox-chat-body" ref={scrollRef} data-testid="inbox-chat-body">
              {messages.length === 0 ? (
                <div className="inbox-no-messages">No messages yet</div>
              ) : messages.map((m, i) => {
                const isOut = m.direction === "outgoing";
                return (
                  <div key={m.id || i} className={`inbox-msg ${isOut ? "out" : "in"}`}>
                    <div className="inbox-msg-bubble">
                      {m.body || "[template]"}
                      {m.template_name && <div className="inbox-msg-tmpl">Template: {m.template_name}</div>}
                      <div className="inbox-msg-meta">
                        {formatTimeShort(m.wa_timestamp)}
                        {isOut && (m.status === "read" ? <CheckCheck className="w-3 h-3 text-blue-500" /> : m.status === "delivered" ? <CheckCheck className="w-3 h-3 opacity-60" /> : <Check className="w-3 h-3 opacity-60" />)}
                        {m.status === "simulated" && <Bot className="w-3 h-3 opacity-60" title="Simulated" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="inbox-quick-replies" data-testid="inbox-quick-replies">
              {quickReplies.map((qr, i) => (
                <button key={qr.id || i} onClick={() => setDraft(qr.text || "")} className="inbox-qr-btn" data-testid={`inbox-qr-${i}`} title={qr.text}>
                  {qr.label || (qr.text || "").substring(0, 30)}
                </button>
              ))}
            </div>

            <div className="inbox-composer">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleMediaUpload}
                accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                style={{ display: "none" }}
                data-testid="inbox-media-file"
              />
              <button
                type="button"
                className="inbox-paperclip"
                onClick={handleMediaPick}
                disabled={uploadingMedia || !selected?.id}
                title="Attach image, video or document"
                data-testid="inbox-paperclip"
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 36, height: 36, borderRadius: 8, border: "1px solid #d1d5db",
                  background: "#f9fafb", color: "#374151", cursor: uploadingMedia ? "wait" : "pointer",
                  marginRight: 4,
                }}
              >
                {uploadingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </button>
              <textarea
                placeholder="Type your message…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                rows={1}
                data-testid="inbox-composer"
              />
              <Button onClick={handleSend} disabled={!draft.trim() || sending} data-testid="inbox-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </>
        )}
      </main>

      {/* ─── Right rail: lead context ─── */}
      {selected && (
        <aside className="inbox-context" data-testid="inbox-context">
          <div className="inbox-context-header">
            <h3>Lead details</h3>
            <button onClick={() => navigate(`/demo/lead/${selected.id}`)} className="inbox-context-more" data-testid="inbox-context-more">
              <ExternalLink className="w-3 h-3" /> Full view
            </button>
          </div>
          <div className="inbox-context-section">
            <div className="inbox-ctx-row"><span>Status</span><strong>{prettyStatus(selected.status)}</strong></div>
            {selected.targetRole && <div className="inbox-ctx-row"><span>Target role</span><strong>{selected.targetRole}</strong></div>}
            {selected.targetMarket && <div className="inbox-ctx-row"><span>Market</span><strong>{selected.targetMarket}</strong></div>}
            {selected.scheduledAt && (
              <div className="inbox-ctx-row">
                <span>Demo</span>
                <strong>{formatDateTime(selected.scheduledAt)}</strong>
              </div>
            )}
            {selected.recommendedPlan && (
              <div className="inbox-ctx-row">
                <span>Recommended</span>
                <strong>{prettyPlan(selected.recommendedPlan)}</strong>
              </div>
            )}
            {(selected.selectedPlan || selected.planAmount || selected.paymentStatus) && (
              <div className="inbox-ctx-row">
                <span>Plan & Pay</span>
                <strong style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {selected.selectedPlan ? prettyPlan(selected.selectedPlan) : "—"}
                  {selected.planAmount ? <span>· ${Number(selected.planAmount).toLocaleString()}</span> : null}
                  <span
                    data-testid="inbox-payment-chip"
                    style={{
                      fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                      background: selected.paymentStatus === "paid" ? "#dcfce7" : selected.paymentStatus === "refunded" ? "#fee2e2" : "#fef3c7",
                      color: selected.paymentStatus === "paid" ? "#166534" : selected.paymentStatus === "refunded" ? "#991b1b" : "#92400e",
                    }}
                  >
                    {(selected.paymentStatus || "pending").toUpperCase()}
                  </span>
                </strong>
              </div>
            )}
            {selected.closerName && (
              <div className="inbox-ctx-row"><span>Closer</span><strong>{selected.closerName}</strong></div>
            )}
            {selected.objectionTags?.length > 0 && (
              <div className="inbox-ctx-row inbox-ctx-tags">
                <span>Objections</span>
                <div className="inbox-ctx-chips">
                  {selected.objectionTags.map(t => <span key={t} className="inbox-ctx-chip">{t}</span>)}
                </div>
              </div>
            )}
          </div>
          {selected.notes && (
            <div className="inbox-context-notes">
              <div className="inbox-ctx-label">Notes</div>
              <p>{selected.notes}</p>
            </div>
          )}
          {selected.fathomSummary && (
            <div className="inbox-context-notes">
              <div className="inbox-ctx-label">Call summary</div>
              <p>{selected.fathomSummary.substring(0, 280)}{selected.fathomSummary.length > 280 ? "…" : ""}</p>
            </div>
          )}
        </aside>
      )}
    </div>
  );
}

// ─── Helpers ───
function initials(n) {
  if (!n) return "?";
  const parts = n.trim().split(" ");
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}
function hashColor(str) {
  const colors = ["#7F77DD", "#EC4899", "#f59e0b", "#10b981", "#3b82f6", "#D85A30"];
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xfffffff;
  return colors[h % colors.length];
}
function relativeTime(iso) {
  if (!iso) return "";
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
function formatTimeShort(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}
function formatDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function prettyStatus(s) {
  const map = { new: "new", upcoming: "upcoming", demo_done: "demo done", demo_in_progress: "in progress", payment_sent: "payment sent", converted: "won", cold: "cold", no_show: "no-show" };
  return map[s] || s;
}
function prettyPlan(p) {
  if (!p) return "—";
  return p.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
